import { useCallback } from 'react';
import { FormMode } from '@sib/utils';
import { DetailsModalView } from '@sidecar/common/components/DetailsModal';
import { useTaskRouteNavigation } from '../use-task-route-navigation';
import {
  isTaskCreateRoute,
  isTaskEditRoute,
  TASK_ROUTE_KEY,
} from '../task-route.utils';

interface UseTaskModalActionsProps {
  navigateToRoute: ReturnType<typeof useTaskRouteNavigation>['navigateToRoute'];
  navigateToReturnOrRoot: ReturnType<
    typeof useTaskRouteNavigation
  >['navigateToReturnOrRoot'];
  pathname: string;
  taskId?: string;
}

const useTaskModalActions = ({
  navigateToRoute,
  navigateToReturnOrRoot,
  pathname,
  taskId,
}: UseTaskModalActionsProps) => {
  const openTask = useCallback(
    (id: string) =>
      navigateToRoute(TASK_ROUTE_KEY.details, {
        taskId: id,
        ...(!taskId && { returnTo: pathname }),
      }),
    [navigateToRoute, pathname, taskId]
  );

  const openCreate = useCallback(
    () => navigateToRoute(TASK_ROUTE_KEY.new),
    [navigateToRoute]
  );

  const close = useCallback(
    () => navigateToReturnOrRoot(),
    [navigateToReturnOrRoot]
  );

  const setView = useCallback(
    (nextView: DetailsModalView) => {
      if (!taskId) return;

      navigateToRoute(
        nextView === DetailsModalView.Form
          ? TASK_ROUTE_KEY.edit
          : TASK_ROUTE_KEY.details,
        { taskId }
      );
    },
    [navigateToRoute, taskId]
  );

  return { openTask, openCreate, close, setView };
};

export const useTaskDetailsModalActions = () => {
  const { params, navigateToRoute, navigateToReturnOrRoot, pathname } =
    useTaskRouteNavigation();

  return useTaskModalActions({
    navigateToRoute,
    navigateToReturnOrRoot,
    pathname,
    taskId: params.taskId,
  });
};

export const useTaskDetailsModalRouteParams = () => {
  const { params, pathname, navigateToRoute, navigateToReturnOrRoot } =
    useTaskRouteNavigation();
  const { taskId } = params;
  const { openTask, openCreate, close, setView } = useTaskModalActions({
    navigateToRoute,
    navigateToReturnOrRoot,
    pathname,
    taskId,
  });

  const isCreate = isTaskCreateRoute(pathname);
  const isOpen = Boolean(taskId) || isCreate;
  const type = taskId ? FormMode.Edit : FormMode.Create;
  const view =
    isCreate || isTaskEditRoute(pathname)
      ? DetailsModalView.Form
      : DetailsModalView.Details;

  return {
    isOpen,
    taskId,
    type,
    view,
    openTask,
    openCreate,
    setView,
    close,
  };
};
